import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { getMediumLabel, getConditionLabel, getTypeOfArtLabel } from "../utils/artkivLabels";

function getImageWithFallback(artwork, fallback = "/placeholder.jpg") {
  if (!artwork) return fallback;
  if (typeof artwork.image_url === "string" && artwork.image_url.trim() !== "") {
    return artwork.image_url;
  }
  if (artwork.images) {
    if (typeof artwork.images === "string") {
      try {
        const parsedImages = JSON.parse(artwork.images);
        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
          return parsedImages[0];
        }
      } catch (e) {
        console.error("Erreur parsing:", e);
      }
    } else if (Array.isArray(artwork.images) && artwork.images.length > 0) {
      const firstImage = artwork.images[0];
      if (typeof firstImage === "object" && firstImage !== null) {
        return firstImage.url || firstImage.path || firstImage;
      }
      return firstImage;
    }
  }
  const possibleFields = [
    "image",
    "img_url",
    "thumbnail_url",
    "photo_url",
  ];
  for (const field of possibleFields) {
    if (typeof artwork[field] === "string" && artwork[field].trim() !== "") {
      return artwork[field];
    }
  }
  return fallback;
}

const OeuvresenventeDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "fr";

  const [artwork, setArtwork] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confidentialChecked, setConfidentialChecked] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchArtwork = async () => {
      const { data, error } = await supabase
        .from("artworks")
        .select("*")
        .eq("id", id)
        .single();

      if (error || !data) {
        setError(t("sale.notFound"));
      } else {
        if (!data.is_public_for_sale && data.client_id !== user.id) {
          setError(t("sale.notAvailable"));
        } else {
          setArtwork(data);
        }
      }
      setIsLoading(false);
    };
    fetchArtwork();
  }, [id, user, t]);

  const handleRequestInfo = () => {
    if (!confidentialChecked) {
      toast.error(t("sale.request.errorCheckbox"));
      return;
    }
    toast.success(t("sale.request.success"));
  };

  if (isLoading) return <div className="p-6">{t("sale.loading")}</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!artwork) return <div className="p-6 text-red-500">{t("sale.none")}</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-serif">{artwork.title}</h1>
        <p className="text-sm text-gray-600 italic mt-1">{artwork.artist}</p>
      </div>

      <div className="rounded-lg overflow-hidden mb-8">
        <img
          src={getImageWithFallback(
            artwork,
            "/images/artworks/Image_principale_Image_Not_Available.png"
          )}
          alt={artwork.title || t("sale.imageAlt")}
          className="w-full h-[400px] object-cover"
          onError={(e) => {
            e.target.src =
              "/images/artworks/Image_principale_Image_Not_Available.png";
          }}
        />
      </div>

      <div className="text-center text-sm text-gray-700 mb-8">
        {artwork.year && (
          <p>
            {t("sale.yearMedium", {
              year: artwork.year,
              medium: getMediumLabel(artwork.medium, t),
            })}
          </p>
        )}

        {artwork.dimensions && (
          <p>{t("sale.dimensions", { value: artwork.dimensions })}</p>
        )}

        <p className="text-green-700 font-semibold mt-2">
          {t("sale.price.label")}: {artwork.sale_price
            ? new Intl.NumberFormat(locale, { style: "currency", currency: artwork.currency || "EUR" }).format(Number(artwork.sale_price))
            : t("sale.price.undefined")}
          <span className="text-xs italic"> {t("sale.price.note")}</span>
        </p>

        <p className="italic mt-1">{t("sale.options")}</p>
      </div>

      <div className="border-t pt-6 mt-6">
        <h2 className="text-xl font-serif mb-4">{t("sale.details.title")}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <p>
            <strong>{t("sale.details.type")} :</strong>{" "}
            {getTypeOfArtLabel(artwork.type_of_art || artwork.typeOfArt || artwork.type, t)}
          </p>
          <p>
            <strong>{t("sale.details.technique")} :</strong>{" "}
            {artwork.medium ? getMediumLabel(artwork.medium, t) : "—"}
          </p>
          <p>
            <strong>{t("sale.details.dimensions")} :</strong>{" "}
            {artwork.dimensions || "—"}
          </p>
          <p>
            <strong>{t("sale.details.year")} :</strong>{" "}
            {artwork.year || "—"}
          </p>
          <p>
            <strong>{t("sale.details.subject")} :</strong>{" "}
            {artwork.subject || "—"}
          </p>
          <p>
            <strong>{t("sale.details.signature")} :</strong>{" "}
            {artwork.signature_details || artwork.signature || "—"}
          </p>
          <p>
            <strong>{t("sale.details.condition")} :</strong>{" "}
            {artwork.condition ? getConditionLabel(artwork.condition, t) : "—"}
          </p>
        </div>

        <div className="mt-6">
          <h3 className="font-semibold">{t("sale.description.title")}</h3>
          <p className="text-sm mt-1">{artwork.description || "—"}</p>
        </div>

        <div className="mt-6">
          <h3 className="font-semibold">{t("sale.conditionNotes.title")}</h3>
          <p className="text-sm mt-1">{artwork.condition_notes || "—"}</p>
        </div>

        <div className="mt-6">
          <h3 className="font-semibold">{t("sale.tags.title")}</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {(artwork.tags || []).map((tag, index) => (
              <span
                key={index}
                className="bg-gray-200 px-2 py-1 rounded-full text-xs"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t pt-6 mt-10">
        <h2 className="text-xl font-serif mb-4">
          {t("sale.request.title")}
        </h2>

        <label className="block text-sm mb-4">
          <input
            type="checkbox"
            checked={confidentialChecked}
            onChange={(e) => setConfidentialChecked(e.target.checked)}
            className="mr-2"
          />
          {t("sale.request.checkbox")}
        </label>

        <button
          onClick={handleRequestInfo}
          className="btn-artsy btn-primary-artsy"
        >
          {t("sale.request.button")}
        </button>
      </div>
    </div>
  );
};

export default OeuvresenventeDetail;
